import React from "react";
import ColorHash from "color-hash";
import "./ContactAvatar.scss";
import ContactConnectedProfilePopup from "../../contacts/components/ContactConnectedProfilePopup";
import { PermitsContact } from "../types/PermitsContact";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-solid-svg-icons";
import { Person } from "../../../queries/contacts";
import { ContactFieldType } from "../../../queries/crmTopics";
import { useMedia } from "react-media";
import classNames from "classnames";

type Contact = Pick<PermitsContact, "userId" | "firstName" | "lastName"> & {
  contactType: PermitsContact["contactType"] | null;
};
type BaseContactData = {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  contact?: Contact;
  anonymous?: boolean;
  disconnected?: boolean;
};
type ContactAvatarProps = (BaseContactData &
  (
    | { firstName: string; lastName: string }
    | { fullName: string }
    | { contact: Contact }
    | { anonymous: boolean }
    | { disconnected: boolean }
  )) & {
  linkToConnectedCitizen?: boolean;
  subscriberContactMethodId?: number;
  onClose?: () => void;
  size?: string;
  colorOverride?: string;
  fontScale?: string;
  genericAvatarSize?: string; // For anonymous/disconnected modes
  className?: string;
  showPopupBanner?: boolean;
  enableEdit?: boolean;
  triggerDelay?: number;
  onEdit?: (data: Person) => void;
  editRequiredFields?: ContactFieldType[];
  open?: boolean;
  popupPosition?:
    | "bottom left"
    | "top left"
    | "top right"
    | "bottom right"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center";
  /** If true, aria-hidden will be computed based on whether the avatar is a
   * link */
  autoAriaHidden?: boolean;
};

export const ContactAvatar = ({
  firstName,
  lastName,
  fullName,
  contact,
  linkToConnectedCitizen,
  subscriberContactMethodId,
  showPopupBanner,
  enableEdit,
  onClose,
  onEdit,
  size,
  fontScale,
  genericAvatarSize = "11px",
  anonymous,
  disconnected,
  open,
  colorOverride,
  triggerDelay,
  popupPosition,
  editRequiredFields,
  autoAriaHidden = true,
  ...rest
}: ContactAvatarProps) => {
  const hasHover = useMedia({ query: "(hover: hover)" });
  let name: null | string = null;
  if (contact) name = `${contact.firstName?.trim()} ${contact.lastName?.trim()}`.trim();
  if (firstName || lastName) name = `${firstName?.trim()} ${lastName?.trim()}`.trim();
  if (fullName) name = fullName.trim();

  name = name ?? "Default";

  const initials =
    !anonymous && !disconnected ? (
      name
        ?.replaceAll(/[\(\);]/g, "")
        ?.split(" ")
        .map((n) => n?.[0]?.toUpperCase() ?? "")
        .join("")
    ) : (
      <FontAwesomeIcon
        icon={faUser}
        className={classNames("tw-m-0 tw-mb-px tw-p-0", {
          "tw-text-black": disconnected,
        })}
        style={{ width: genericAvatarSize, height: genericAvatarSize }}
      />
    );

  // Color restrictions are set to ensure sufficient contrast ratios for accessibility
  const colorHash = new ColorHash({
    hue: { min: 0, max: 360 },
    saturation: 0.5,
    lightness: [0.25, 0.275, 0.3],
  });
  const color: string = colorOverride ?? (anonymous ? "#737373" : disconnected ? "#F0F0F0" : colorHash.hex(name));
  const ariaHidden = !linkToConnectedCitizen ? "true" : "false"; // only display if the avatar is interactable

  const avatar = (
    <div {...(autoAriaHidden && { "aria-hidden": ariaHidden })} {...rest}>
      <div
        className='contact-avatar'
        style={{
          backgroundColor: color,
          width: size ?? "32px",
          height: size ?? "32px",
          fontSize: fontScale ?? "90%",
        }}
      >
        <span>{initials}</span>
      </div>
    </div>
  );

  if (linkToConnectedCitizen && (hasHover || open)) {
    return (
      <ContactConnectedProfilePopup
        contact={contact}
        trigger={avatar}
        triggerDelay={triggerDelay}
        showBanner={showPopupBanner}
        enableEdit={enableEdit}
        onEdit={onEdit}
        open={open}
        subscriberContactMethodId={subscriberContactMethodId}
        onClose={onClose}
        popupPosition={popupPosition}
        editRequiredFields={editRequiredFields}
      />
    );
  }
  return avatar;
};
